import React from 'react';
import styled, { css } from 'styled-components';

import { ICON_TYPES, POSITIONS } from '../../../shared/constants';

import CloseTextLink from '../../atoms/close-text-link/CloseTextLink';
import { ArrowReload } from '../../atoms/icons/ArrowReload';
import StepCounter from '../../atoms/step-counter/StepCounter';

import { DEQ_TYPES } from '../../../utils/deq';
import { LABELS } from '../../../utils/language/form-labels';
import languageHelper from '../../../utils/language/language-helper';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { Body, H2, SmallText } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import ContentWrapper from '../content-wrapper/ContentWrapper';

export const CANCEL_TYPE = {
  close: 'close',
  back: 'back',
};

type Steps = {
  current: number;
  total: number;
};

export interface StepHeaderProps {
  label?: string;
  title?: string;
  cancelUrl?: string;
  cancelLabel?: string;
  cancelType?: string;
  steps?: Steps;
  enableReset?: boolean;
  onResetSteps?: () => void;
  iframePaddingEnabled?: boolean;
}

const StepHeader = ({
  label,
  title,
  cancelUrl,
  cancelLabel,
  cancelType = CANCEL_TYPE.close,
  steps,
  enableReset,
  onResetSteps,
  iframePaddingEnabled = false,
}: StepHeaderProps) => {
  const { lang } = languageHelper();

  return (
    <FormHeader>
      <StyledContentWrapper morePadding={iframePaddingEnabled}>
        <AlignContent>
          <Inner
            positionItems={
              cancelType === CANCEL_TYPE.close ? 'row' : 'column-reverse'
            }
          >
            <HeaderText>
              {label && cancelType === CANCEL_TYPE.close && (
                <Label data-testid="label">{label}</Label>
              )}
              {title && (
                <Title
                  data-testid="step-header-title"
                  data-pw-testid="step-header-title"
                >
                  {title}
                </Title>
              )}
              {steps && steps.current <= steps.total && (
                <StepContainer>
                  <StepCounter
                    currentStep={steps.current}
                    maxAmountOfSteps={steps.total}
                  />
                </StepContainer>
              )}
            </HeaderText>
            {cancelUrl && (
              <CloseTextLink
                text={cancelLabel || LABELS.cancel[lang]}
                href={cancelUrl}
                iconType={
                  cancelType === CANCEL_TYPE.back
                    ? ICON_TYPES.chevronLeft
                    : CANCEL_TYPE.close
                }
                parentCompType="contact-form"
                color={Colors.abnGreen300}
                align={POSITIONS.left}
              />
            )}
            {enableReset && onResetSteps && (
              <ReloadWizardContainer
                onClick={onResetSteps}
                data-component-type={DEQ_TYPES.container}
                data-element-type={DEQ_TYPES.button}
                data-element-label="Opnieuw invullen"
              >
                <Body>Opnieuw invullen</Body>
                <StyledArrowReload />
              </ReloadWizardContainer>
            )}
          </Inner>
        </AlignContent>
      </StyledContentWrapper>
    </FormHeader>
  );
};

export default StepHeader;

const AlignContent = styled.div`
  display: flex;
  justify-content: left;

  ${mq.from.breakpoint.M`
    width: 736px;
  `}
`;

const FormHeader = styled.header`
  background-color: ${Colors.abnMutedGreen55};
  position: relative;
`;

const StyledContentWrapper = styled(ContentWrapper)<{ morePadding?: boolean }>`
  flex-direction: row;
  justify-content: center;
  padding-bottom: ${Spacings.S};
  padding-top: ${Spacings.S};

  ${mq.from.breakpoint.M`
    display: flex;
  `}

  ${mq.from.breakpoint.L`
    align-items: left;
  `}

  ${({ morePadding }) =>
    morePadding
      ? css`
          ${mq.from.breakpoint.M`
            padding: ${Spacings.M} 54px;
          `}
        `
      : css`
          ${mq.from.breakpoint.M`
            padding: ${Spacings.M};
          `}
        `}
`;

const HeaderText = styled.div`
  flex-grow: 1;
`;

const Title = styled(H2)`
  color: ${Colors.abnBlack};
  margin: 0;

  ${mq.from.breakpoint.M`
    margin: 0 124px ${Spacings.XS} 0;
  `}
`;

const Label = styled(SmallText)`
  color: ${Colors.abnNeutral400};
  flex-grow: 1;
  margin: 0 0 ${Spacings.XS} 0;
`;

const Inner = styled.div<{ positionItems: string }>`
  display: flex;
  flex-direction: column;
  row-gap: ${Spacings.XS};
  width: 100%;

  ${mq.from.breakpoint.M`
    flex-direction: ${({ positionItems }: { positionItems: string }) =>
      positionItems};
    justify-content: space-between;
  `}
`;
const StepContainer = styled.div`
  display: block;
  padding-top: 8px;
`;

const ReloadWizardContainer = styled.div`
  align-items: center;
  color: ${Colors.abnGreen300};
  cursor: pointer;
  display: flex;
  flex-direction: row;
  font-size: 16px;
  gap: ${Spacings.XXS};
  margin-top: ${Spacings.XS};

  ${mq.from.breakpoint.M`
    margin-top: 0;
  `}
`;

const StyledArrowReload = styled(ArrowReload)`
  min-width: fit-content;
`;
